import { useState } from 'react';
import { typographyClasses } from './variants';

type typographyType = 'title' | 'titleSection' | 'lead' | 'p' | 'display';
type typographySizes =
  | 'default'
  | 'small'
  | 'mini'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6';

type TypographyOwnProps<E extends React.ElementType = React.ElementType> = {
  children?: React.ReactNode;
  as?: E;
};

type iTypographyProps<E extends React.ElementType> = TypographyOwnProps<E> &
  Omit<React.ComponentProps<E>, keyof TypographyOwnProps> & {
    type?: typographyType;
    size?: typographySizes;
    variant?: 'normal' | 'italic';
    weight?: 'normal' | 'bold';
    showViewMoreButton?: boolean;
    viewMoreMaxTextLength?: number;
  };

const DEFAULT_ELEMENT = 'p';

function Typography<E extends React.ElementType = typeof DEFAULT_ELEMENT>({
  children,
  type = 'p',
  size,
  className = '',
  variant = 'normal',
  weight = 'normal',
  as,
  showViewMoreButton = false,
  viewMoreMaxTextLength = 180,
  ...rest
}: iTypographyProps<E>) {
  const [isViewMore, setIsViewMore] = useState(false);
  const componentClassNames = `${typographyClasses({
    size,
    type,
    variant,
    weight,
    className,
  })}`;

  let newClassNames = componentClassNames;

  let MyComponent = as || DEFAULT_ELEMENT;

  if (type === 'title' || type === 'titleSection' || type === 'display') {
    const allowedSizes = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'];

    if (!size || !allowedSizes.includes(size)) {
      MyComponent = 'h1' as E;
    } else {
      MyComponent = size as E;
    }

    newClassNames = `${componentClassNames} ${size ? ` text-${size}` : ''}`;
  }

  const allowedSizes = ['default', 'small', 'mini'];

  if (!size || !allowedSizes.includes(size) || type === 'lead') {
    size = 'default';
  }

  function getPreviewText(text: string) {
    let previewText = '';
    text.split(' ').forEach((word) => {
      if (previewText.length < viewMoreMaxTextLength) {
        previewText += `${word} `;
      }
    });

    const hasDots = previewText.length < text.length;

    return {
      text: children,
      previewText: `${previewText.trim()}${hasDots ? '...' : ''}`,
      hasDots,
    };
  }

  if (showViewMoreButton && typeof children === 'string') {
    const { previewText, text, hasDots } = getPreviewText(children);

    return (
      <MyComponent className={newClassNames} {...rest}>
        {isViewMore ? text : previewText}{' '}
        {hasDots && (
          <span
            className='text-primary-500 cursor-pointer'
            onClick={() => {
              setIsViewMore(!isViewMore);
            }}
          >
            {isViewMore ? 'Ver menos' : 'Ver mais'}
          </span>
        )}
      </MyComponent>
    );
  }

  return (
    <MyComponent className={newClassNames} {...rest}>
      {children}
    </MyComponent>
  );
}

export { Typography };
export type { iTypographyProps };

